import {Pipe, PipeTransform} from '@angular/core';
import {Pushpin} from '../classes/pushpin.class';

type Box = { south: number, west: number, north: number, east: number };

/**
 * Filters the pushpins down to those that fall inside the map viewport, so that only those end up in the DOM.
 * The viewport is padded a bit so that pins just outside the screen are already present while panning.
 */
@Pipe({
    name: 'pushpinsInBounds'
})
export class PushpinsInBoundsPipe implements PipeTransform {
    private static readonly PADDING = 0.25;

    // Lengthmarks can have a lot of snapped points, so calculate their box only once
    private readonly boxCache = new WeakMap<Pushpin, Box | null>();

    transform(pushpins: Pushpin[] | null | undefined, bounds: google.maps.LatLngBounds | null | undefined): Pushpin[] {
        if (!pushpins?.length) {
            return [];
        }
        if (!bounds) {
            return pushpins;
        }

        const view = this.padded(bounds.toJSON());
        return pushpins.filter(pushpin => {
            const box = this.boxOf(pushpin);
            return !!box && box.south <= view.north && box.north >= view.south && box.west <= view.east && box.east >= view.west;
        });
    }

    private padded(box: Box): Box {
        const latPadding = (box.north - box.south) * PushpinsInBoundsPipe.PADDING;
        let west = box.west;
        let east = box.east;
        // A viewport crossing the antimeridian has west > east, which is not worth supporting here: show everything on that axis
        if (west > east) {
            west = -180;
            east = 180;
        } else {
            const lngPadding = (east - west) * PushpinsInBoundsPipe.PADDING;
            west -= lngPadding;
            east += lngPadding;
        }
        return {south: box.south - latPadding, north: box.north + latPadding, west, east};
    }

    private boxOf(pushpin: Pushpin): Box | null {
        if (this.boxCache.has(pushpin)) {
            return this.boxCache.get(pushpin);
        }

        const points: { lat: number, lng: number }[] = [{lat: +pushpin.lat, lng: +pushpin.lng}];
        if (pushpin.lengthmark) {
            pushpin.snappedpoints?.snappedPoints?.forEach(p => points.push({lat: +(p.lat ?? p.latitude), lng: +(p.lng ?? p.longitude)}));
        }

        let box: Box | null = null;
        for (const {lat, lng} of points) {
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                continue;
            }
            box = box
                ? {south: Math.min(box.south, lat), north: Math.max(box.north, lat), west: Math.min(box.west, lng), east: Math.max(box.east, lng)}
                : {south: lat, north: lat, west: lng, east: lng};
        }

        this.boxCache.set(pushpin, box);
        return box;
    }
}
